import { z } from "zod";
import { EServiceType } from "../common";
import { geoLocationSchema } from "../geo";
import { addressSchema } from "../location";
import { petSchema } from "../pet";
import {
  productImageResponseSchema,
  productOptionResponseSchema,
  productVariantResponseSchema,
} from "../product";
import { userDataSchema } from "../user";
import { EProductType, EProductSubType } from "../../constants/product.constants";
import { paginationSchema } from "../pagination";
import { paginationMetaSchema } from "../base-api-response";

export enum SubscriptionTypeV4 {
  WALKING = "WALKING",
  GROOMING = "GROOMING",
  TRAINING = "TRAINING",
}

export enum SubscriptionStatusV4 {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
  PAUSED = "PAUSED",
  COMPLETED = "COMPLETED",
  EXPIRED = "EXPIRED",
}

/**
 * Pause / reschedule reasons for subscription change-requests (v4 API).
 */
export enum SubscriptionPauseReason {
  ADMIN_DOG_UNWELL = "ADMIN_DOG_UNWELL",
  ADMIN_ON_TRIP = "ADMIN_ON_TRIP",
  ADMIN_SHORT_BREAK = "ADMIN_SHORT_BREAK",
  ADMIN_PAUSE_OTHER = "ADMIN_PAUSE_OTHER",
  CUSTOMER_DOG_UNWELL = "CUSTOMER_DOG_UNWELL",
  CUSTOMER_ON_TRIP = "CUSTOMER_ON_TRIP",
  CUSTOMER_SHORT_BREAK = "CUSTOMER_SHORT_BREAK",
  CUSTOMER_PAUSE_OTHER = "CUSTOMER_PAUSE_OTHER",
}

/**
 * Subscription change-request action (v4 API).
 */
export enum SubscriptionChangeAction {
  CANCEL_SUBSCRIPTION = "CANCEL_SUBSCRIPTION",
  PAUSE_SUBSCRIPTION = "PAUSE_SUBSCRIPTION",
  RESUME_SUBSCRIPTION = "RESUME_SUBSCRIPTION",
}

export enum PaymentMethod {
  COD = "COD",
  CARD = "CARD",
  UPI = "UPI",
  NET_BANKING = "NET_BANKING",
  EXTERNAL_PAYMENT = "EXTERNAL_PAYMENT",
}

export enum PaymentStatusV4 {
  PENDING = "PENDING",
  /** Booking fee collected online; remaining balance due (e.g. BOOKING_FEE subscriptions). */
  PARTIALLY_COLLECTED = "PARTIALLY_COLLECTED",
  COLLECTED = "COLLECTED",
  VERIFIED = "VERIFIED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

export const subscriptionProductSchemaV4 = z.object({
  id: z.string(),
  title: z.string(),
  type: z.nativeEnum(EProductType).nullish(),
  subType: z.nativeEnum(EProductSubType).nullish(),
  code: z.string().optional(),
  price: z.number().optional(),
  compareAtPrice: z.number().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  tags: z.array(z.string()).optional(),
  status: z.string(),
  variant: productVariantResponseSchema,
  options: z.array(productOptionResponseSchema),
  images: z.array(productImageResponseSchema),
  meta: z.record(z.unknown()).optional(),
  quantity: z.number().optional(),
});

export const paymentSchemaV4 = z.object({
  method: z.nativeEnum(PaymentMethod),
  amount: z.number(),
  status: z.nativeEnum(PaymentStatusV4)
});

/** Mirrors backend `sessionsSummaryResponseSchema` — returned by the admin subscription detail API. */
export const sessionsSummarySchemaV4 = z.object({
  totalSessions: z.number(),
  completedSessions: z.number(),
  upcomingSessions: z.number(),
  cancelledSessions: z.number(),
  rescheduledSessions: z.number(),
});

export type SessionsSummaryV4 = z.infer<typeof sessionsSummarySchemaV4>;

export const subscriptionSchemaV4 = z.object({
  id: z.string(),
  legacySubscriptionId: z.string().optional(),
  user: userDataSchema,
  address: addressSchema,
  serviceCategory: z.nativeEnum(SubscriptionTypeV4),
  serviceType: z.nativeEnum(EServiceType),
  status: z.nativeEnum(SubscriptionStatusV4),
  startsAt: z.number(),
  endsAt: z.number(),
  products: z.array(subscriptionProductSchemaV4),
  pets: z.array(petSchema),
  geoLocation: geoLocationSchema,
  schedule: z.array(z.number()),
  metadata: z.record(z.unknown()),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  noOfSessions: z.number().nullish(),
  /** Only returned by the admin detail endpoint; list responses omit it. */
  sessionsSummary: sessionsSummarySchemaV4.nullish(),
  paymentMethod: z.nativeEnum(PaymentMethod),
  orderId: z.string(),
  payment: paymentSchemaV4,
});

export const getUserSubscriptionsQuerySchema = paginationSchema.extend({
  serviceCategory: z.nativeEnum(SubscriptionTypeV4).optional(),
  status: z.nativeEnum(SubscriptionStatusV4).optional(),
  serviceType: z.nativeEnum(EServiceType).optional(),
  cityCode: z.string().trim().min(1).optional(),
  subCityCode: z.string().trim().min(1).optional(),
  hubCode: z.string().trim().min(1).optional(),
  createdAtFrom: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
  createdAtTo: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
  validAtFrom: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
  validAtTo: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),
  search: z.string().trim().min(1).optional(),
  userId: z.string().trim().min(1).optional(),
  orderId: z.string().trim().min(1).optional(),
});

export const getUserSubscriptionsResponseSchema = z.object({
  items: z.array(subscriptionSchemaV4),
  meta: paginationMetaSchema,
});

export type GetUserSubscriptionsQuery = z.infer<typeof getUserSubscriptionsQuerySchema>;
export type GetUserSubscriptionsResponse = z.infer<typeof getUserSubscriptionsResponseSchema>;
export type ISubscriptionProductDataV4 = z.infer<typeof subscriptionProductSchemaV4>;
export type ISubscriptionDataV4 = z.infer<typeof subscriptionSchemaV4>;